export const TAB_STYLES = [
	{ label: 'Underline', value: 'underline' },
	{ label: 'Pills', value: 'pills' },
	{ label: 'Boxed', value: 'boxed' },
];

export const TAB_ORIENTATIONS = [
	{ label: 'Horizontal', value: 'horizontal' },
	{ label: 'Vertical', value: 'vertical' },
];

export const TAB_POSITIONS = {
	horizontal: [
		{ label: 'Top', value: 'top' },
		{ label: 'Bottom', value: 'bottom' },
	],
	vertical: [
		{ label: 'Left', value: 'left' },
		{ label: 'Right', value: 'right' },
	],
};

export const TAB_ALIGNMENTS = [
	{ label: 'Start', value: 'start' },
	{ label: 'Center', value: 'center' },
	{ label: 'Stretch', value: 'stretch' },
];

/**
 * Layout presets.
 *
 * A preset is data, not code: it names the regions a tab is split into, and
 * `style.scss` places them from the `layout-<key>` class on the root. Neither
 * renderer branches per preset, so adding one is a CSS block plus an entry
 * here — nothing in Edit or render.php grows.
 *
 * `classic` declares no regions at all. Tab content stays free-form inner
 * blocks exactly as it has always been saved, so existing content keeps working
 * untouched; regions only appear once a preset asks for them.
 *
 * The keys are mirrored by an allowlist in render.php — keep the two in step.
 */

/**
 * What a side region starts with.
 *
 * Switching a tab to a side preset moves everything the tab already holds into
 * the FIRST region, so without this the side area arrives empty — a blank
 * column beside the content, giving no clue what it is for or how wide it is.
 * A region with a `template` is filled from it; regions without one (main) keep
 * receiving the tab's own content.
 *
 * Shared by both side presets rather than restated per preset: the two are the
 * same layout mirrored, so the copy is the same.
 * `createBlocksFromInnerBlocksTemplate` reads a template without mutating it,
 * so one array is safe to hand out repeatedly.
 */
const SIDE_REGION_TEMPLATE = [
	[ 'core/heading', { level: 4, content: 'Good to know' } ],
	[
		'core/paragraph',
		{
			content:
				'Use the side area for the short, useful things that would interrupt the main column.',
		},
	],
	[
		'core/list',
		{},
		[
			[ 'core/list-item', { content: 'A one-line summary of this tab' } ],
			[ 'core/list-item', { content: 'A link to something related' } ],
			[ 'core/list-item', { content: 'Who to contact with questions' } ],
		],
	],
	[
		'core/paragraph',
		{
			content:
				'Each tab keeps its own side area, so this can change from tab to tab.',
		},
	],
];

export const LAYOUT_PRESETS = {
	classic: {
		label: 'Classic',
		regions: [],
	},
	'side-right': {
		label: 'Side area, right',
		regions: [
			{ area: 'main', label: 'Main content' },
			{ area: 'side', label: 'Side content', template: SIDE_REGION_TEMPLATE },
		],
	},
	'side-left': {
		label: 'Side area, left',
		regions: [
			{ area: 'main', label: 'Main content' },
			{ area: 'side', label: 'Side content', template: SIDE_REGION_TEMPLATE },
		],
	},
};

export const LAYOUT_PRESET_OPTIONS = Object.entries( LAYOUT_PRESETS ).map(
	( [ value, preset ] ) => ( { value, label: preset.label } )
);

/**
 * How content enters when a tab is opened.
 *
 * The keys name a starting transform in `style.scss`; the duration, easing and
 * stagger behind them are fixed design tokens rather than controls. Mirrored by
 * an allowlist in render.php — keep the three in step.
 */
export const CONTENT_ANIMATIONS = [
	{ label: 'None', value: 'none' },
	{ label: 'Fade', value: 'fade' },
	{ label: 'Fade up', value: 'fade-up' },
	{ label: 'Slide in', value: 'slide' },
	{ label: 'Zoom', value: 'zoom' },
];

/**
 * Animation classes for a set of attributes.
 *
 * Which targets exist depends on the preset: `classic` animates the panel's
 * content as one piece, the side presets animate each region separately. The
 * outgoing panel's cross-fade is driven by `has-tab-animation`, which is only
 * worth switching on when something is actually animating — with every target
 * set to None the block keeps its original instant swap.
 *
 * Kept here so Edit and render.php agree on the vocabulary; render.php builds
 * the same list in PHP.
 *
 * @param {Object} attributes Block attributes.
 * @return {Array} Class names for the block wrapper.
 */
export function getAnimationClasses( attributes ) {
	const preset = attributes.layoutPreset || 'classic';
	const targets =
		'classic' === preset
			? [ [ 'content', attributes.contentAnimation ] ]
			: [
					[ 'main', attributes.mainAnimation ],
					[ 'side', attributes.sideAnimation ],
			  ];

	const classes = targets
		.filter( ( [ , value ] ) => value && 'none' !== value )
		.map( ( [ target, value ] ) => `anim-${ target }-${ value }` );

	return classes.length ? [ ...classes, 'has-tab-animation' ] : [];
}

/**
 * Build the inner blocks for one tab panel.
 *
 * Returns a fresh array per call so each tab owns its template rather than
 * sharing one object, and so every tab starts with distinct copy — identical
 * placeholder text in every panel makes it impossible to tell at a glance
 * whether switching tabs is actually working.
 *
 * Lead paragraph, bulleted specifics, closing paragraph. A panel holding a
 * single sentence gave the author nothing to work from and made the tab strip
 * look like the taller thing on the page; this shape fills the panel the way a
 * real one is filled.
 *
 * @param {string}   heading Panel heading text.
 * @param {string}   lead    Opening paragraph.
 * @param {string[]} bullets List items.
 * @param {string}   close   Closing paragraph.
 * @return {Array} Inner blocks template for a single tab.
 */
const tabPanel = ( heading, lead, bullets, close ) => [
	[
		'core/group',
		{
			style: {
				spacing: {
					padding: {
						top: '20px',
						right: '20px',
						bottom: '20px',
						left: '20px',
					},
				},
			},
			layout: { type: 'constrained', justifyContent: 'left' },
		},
		[
			[ 'core/heading', { level: 3, content: heading } ],
			[ 'core/paragraph', { content: lead } ],
			[
				'core/list',
				{},
				bullets.map( ( item ) => [ 'core/list-item', { content: item } ] ),
			],
			[ 'core/paragraph', { content: close } ],
		],
	],
];

export const TEMPLATE = [
	[
		'brandy/tab',
		{ label: 'Overview' },
		tabPanel(
			'What this is',
			'Open with the short version — what this is and who it suits. Keep it to a couple of sentences; the detail belongs in the tabs beside this one.',
			[
				'Who it is for, in a single line',
				'The problem it solves',
				'What makes it different from the alternatives',
				'Where to go next if this looks like a fit',
			],
			'Readers who want more will open the next tab. Readers who do not have already got what they came for.'
		),
	],
	[
		'brandy/tab',
		{ label: 'How it works' },
		tabPanel(
			'From first step to finished',
			'Walk through the process in the order someone actually experiences it, one short step at a time.',
			[
				'Get in touch and tell us what you need',
				'We come back with a plan and a fixed quote',
				'Work starts as soon as you approve it',
				'You get progress updates until it is done',
			],
			'If a step usually raises a question, answer it here rather than leaving it for the FAQ tab.'
		),
	],
	[
		'brandy/tab',
		{ label: "What's included" },
		tabPanel(
			'Everything you get',
			'List what someone actually receives, in concrete terms — specifics build more confidence than adjectives do.',
			[
				'Everything described on this page, delivered as described',
				'Setup and handover, with nothing left for you to configure',
				'Documentation written for people rather than for engineers',
				'Support for the first 30 days, by email or phone',
			],
			'Anything that is not included is worth naming too. A surprise after purchase costs more than it saves.'
		),
	],
	[
		'brandy/tab',
		{ label: 'FAQ' },
		tabPanel(
			'Questions we get asked',
			'Collect the questions that come up before someone commits — answering them here removes the last reasons to hesitate.',
			[
				'How long does it take from start to finish?',
				'What happens if I need to change something later?',
				'Is there anything I need to prepare first?',
				'Who do I contact if something goes wrong?',
			],
			'Keep each answer to a couple of sentences, and link out when the full answer runs longer than that.'
		),
	],
];
